
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { SectionWithCitations, CitationReference } from "@/types/citations";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Validates if a string is a valid UUID
 */
export function isUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

/**
 * Check if a string is likely a JSON string
 */
export function isLikelyJsonString(str: string): boolean {
  if (typeof str !== 'string') return false;
  str = str.trim();
  return (str.startsWith('{') && str.endsWith('}')) || 
         (str.startsWith('[') && str.endsWith(']'));
}

/**
 * Helper function to safely attempt parsing JSON
 */
export function safeJsonParse<T>(str: string): T | null {
  try {
    return JSON.parse(str) as T;
  } catch (e) {
    console.debug("Failed to parse JSON string:", e);
    return null;
  }
}

/**
 * Ensures content is in the expected format with proper citation structure
 */
export function ensureContentFormat(content: any, sectionName: string): SectionWithCitations {
  // Case 1: Content already has proper structure
  if (content && typeof content === 'object' && typeof content.content === 'string') {
    // Ensure citations are in correct format
    const validCitations = Array.isArray(content.citations) 
      ? content.citations.filter((citation: any) => 
          typeof citation === 'object' && 
          citation !== null && 
          typeof citation.index === 'number' && 
          typeof citation.text === 'string'
        ).map((citation: any) => ({
          index: Number(citation.index),
          text: String(citation.text)
        }))
      : [];
    
    return {
      content: content.content.trim(),
      citations: validCitations
    };
  } 
  
  // Case 2: Content is just a string
  if (typeof content === 'string') {
    // Try to extract citation references from the string
    const extractedCitations = [];
    const regex = /\[(\d+)\]/g;
    let match;
    
    while ((match = regex.exec(content)) !== null) {
      extractedCitations.push({
        index: parseInt(match[1], 10),
        text: `Reference ${match[1]}`
      });
    }
    
    return {
      content: content.trim(),
      citations: extractedCitations
    };
  }
  
  // Case 3: No valid content provided, return default
  return {
    content: `No ${sectionName.replace(/_/g, ' ')} information available.`,
    citations: []
  };
}
